/* eslint-disable @typescript-eslint/no-explicit-any */
import { useState } from 'react';
import { Message } from 'primereact/message';
import { Button } from 'sharedUi/Button';
import { TextArea } from 'sharedUi/TextArea';
import FormSelect from '../../components/forms/form-select';
import { ragsService } from '../../services/rags.service';
import { extractErrorMessage } from '../../utils/error-handler';
import type { RagQueryResult, RagVersion } from '../../types';
import { versionTone } from './rag-status';
import { EmptyState, Metric, Rise, SectionLabel, StatusChip } from '../../components/common/panel';
import { PANEL, PRETTY, riseAt } from '../../components/common/ui-kit';
import { collectDocImages } from '../../utils/source-images';
import ChunkImages from './chunk-images';
import { newTraceId } from '../../services/api';
import AiFeedbackBar from '../../components/ai-feedback/ai-feedback-bar';

interface Props { ragId: string; ragCode: string; versions: RagVersion[]; activeVersion?: number | null; canQuery: boolean }

/** Spec §20.3 — enter a question, inspect the complete runtime behaviour. */
/** Sentinel for "whatever is live right now", so the field is never empty. */
const ACTIVE_VERSION = 'active';

const RagPlayground = ({ ragId, ragCode, versions, activeVersion, canQuery }: Props) => {
  const [question, setQuestion] = useState('');
  // 'active' rather than '': a float label reads an empty value as no value and
  // sits on top of the selected option instead of floating above it.
  const [version, setVersion] = useState<string>(ACTIVE_VERSION);
  const [result, setResult] = useState<RagQueryResult | null>(null);
  /* A RAG query persists nothing, so the request's own trace id is what feedback
     on this answer points at — see AiFeedbackBar below. Minted before the call
     rather than read back from the response: the browser cannot read the header
     cross-origin, and the middleware honours an inbound id. */
  const [traceId, setTraceId] = useState<string | null>(null);
  const [mode, setMode] = useState<'retrieve' | 'query' | null>(null);
  const [error, setError] = useState<string | null>(null);

  const versionOptions = [{ label: `Active${activeVersion != null ? ` (v${activeVersion})` : ' (none)'}`, value: ACTIVE_VERSION },
    ...versions.map((v) => ({ label: `v${v.version} · ${v.status}${v.artifactCode ? ` · ${v.artifactCode}` : ''}`, value: String(v.version) }))];

  const run = async (kind: 'retrieve' | 'query') => {
    if (!question.trim()) return;
    setMode(kind); setError(null); setResult(null);
    const trace = newTraceId();
    setTraceId(trace);
    try {
      const body = { query: question.trim(), version: version === ACTIVE_VERSION ? null : Number(version) };
      setResult(kind === 'retrieve'
        ? await ragsService.retrieve(ragId, body, trace)
        : await ragsService.query(ragId, body, trace));
    } catch (e) {
      setError(extractErrorMessage(e, `${kind === 'retrieve' ? 'Retrieval' : 'Query'} failed`));
    } finally {
      setMode(null);
    }
  };

  const t = result?.timings || {};
  const ms = (n?: number | null) => (n == null ? '—' : `${n} ms`);

  return (
    <div className="flex flex-col gap-3" data-testid="Rag.Playground">
      <Rise className={`${PANEL} flex flex-col gap-3 p-4`}>
        <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
          <div className="md:col-span-2">
            <TextArea label="Question" value={question} onChange={(v: string) => setQuestion(v)} rows={3} placeholder='e.g. "What is the punishment for robbery?"' />
          </div>
          <FormSelect label="Version" name="version" value={version} options={versionOptions} onChange={(e: any) => setVersion(e.target ? e.target.value : e)}
                      helperText="Pick a CANDIDATE to test it before activation" />
        </div>
        <div className="flex justify-end gap-2">
          <span className="ui-press inline-flex">
            <Button label={mode === 'retrieve' ? 'Retrieving…' : 'Retrieve only'} icon={mode === 'retrieve' ? 'pi pi-spin pi-spinner' : 'pi pi-search'} severity="secondary" outlined size="small"
                    disabled={!canQuery || !question.trim() || mode !== null} onClick={() => run('retrieve')} />
          </span>
          <span className="ui-press inline-flex">
            <Button label={mode === 'query' ? 'Asking…' : 'Ask'} icon={mode === 'query' ? 'pi pi-spin pi-spinner' : 'pi pi-send'} size="small"
                    disabled={!canQuery || !question.trim() || mode !== null} onClick={() => run('query')} />
          </span>
        </div>
        {!canQuery && <p className="text-xs text-ink-muted">You need RAG.QUERY to run the playground.</p>}
      </Rise>

      {error && <Message severity="error" className="w-full" text={error} />}

      {result && (
        <>
          {/* What answered, and how long each stage took. Timings are tabular so
              a second run does not shuffle the row as the digits change. */}
          <Rise className={`${PANEL} px-4 py-3`}>
            <div className="flex flex-wrap items-center gap-2">
              <span className="font-mono text-xs text-gray-600 dark:text-gray-300">{ragCode}</span>
              <StatusChip tone={versionTone(result.versionStatus)}>{`v${result.version} · ${result.versionStatus}`}</StatusChip>
              {result.artifactCode && <span className="font-mono text-xs text-ink-muted">{result.artifactCode}</span>}
              {result.generation?.model && (
                <StatusChip tone="neutral" dot={false} className="font-mono">{result.generation.model}</StatusChip>
              )}
            </div>
            <div className={`mt-3 grid grid-cols-2 gap-x-6 gap-y-3 ${result.reranking ? 'sm:grid-cols-5' : 'sm:grid-cols-4'}`}>
              <Metric label="Embed" value={ms(t.embedMs)} />
              <Metric label="Search" value={ms(t.queryMs)} />
              {result.reranking && <Metric label="Rerank" value={ms(t.rerankMs)} />}
              <Metric label="Answer" value={ms(t.generationMs)} />
              <Metric label="Total" value={ms(t.totalMs ?? t.retrievalMs)} />
            </div>
            {result.reranking && (
              result.reranking.applied
                ? <p className="mt-3 text-xs text-ink-muted">
                    Reranked {result.reranking.candidates} candidates with <span className="font-mono">{result.reranking.model}</span>; the chunks below are in its order.
                  </p>
                : <Message severity="warn" className="mt-3 w-full"
                           text={`Reranking is on but was skipped, so chunks are in retrieval order: ${result.reranking.reason || 'unknown reason'}`} />
            )}
          </Rise>

          {result.answer !== undefined && (
            <Rise i={1} className={`${PANEL} p-4`}>
              <SectionLabel icon="pi pi-comment">Answer</SectionLabel>
              {result.answer
                ? <p className="mt-2 max-w-3xl whitespace-pre-wrap text-[15px] leading-relaxed text-ink" style={PRETTY}>{result.answer}</p>
                : <p className="mt-2 text-sm text-ink-muted">Generation is off on this version, so retrieval ran alone.</p>}
              {!!result.citations?.length && (
                <div className="mt-3 flex flex-wrap gap-1.5">
                  {result.citations.map((c) => (
                    <span key={c.index} className="ui-tone ui-tone--info font-mono">
                      <span className="opacity-70">[{c.index}]</span>
                      {c.source || ''}{c.page != null ? ` p.${c.page}` : ''}
                    </span>
                  ))}
                </div>
              )}
              {result.promptRef && (
                <p className="mt-3 text-xs text-ink-muted">Prompt: {result.promptRef.name || result.promptRef.promptId}</p>
              )}

              {/* Under the answer, above the chunks: the answer is what is being
                  judged, and the version is what tells a later reader whether a
                  fix worked. Anchored on the trace because the query itself is
                  not persisted — the one honest id this surface has today. */}
              {result.answer ? (
                <AiFeedbackBar
                  className="mt-4 border-t border-line pt-3"
                  componentType="RAG"
                  componentCode={result.ragCode ? `rag:${result.ragCode}` : ragCode ? `rag:${ragCode}` : undefined}
                  componentId={traceId}
                  componentVersion={`${result.ragCode || ragCode || 'rag'}:v${result.version}`}
                  traceId={traceId}
                  runtimeContext={{
                    userQuery: question.trim() || null,
                    finalResponse: result.answer || null,
                    knowledgeBaseId: result.ragCode || ragCode || null,
                    retrievedDocuments: (result.citations || [])
                      .map((c) => c.source).filter(Boolean),
                    ragCode: result.ragCode || ragCode || null,
                    ragVersion: result.version ?? null,
                    servedModel: result.generation?.model || null,
                    retrievedCount: result.chunks?.length ?? null,
                    estimatedContextTokens: result.estimatedContextTokens ?? null,
                    latencyMs: result.timings?.totalMs ?? null,
                  }}
                  meta={result.generation?.model || undefined}
                  testId="RagPlayground.Feedback"
                />
              ) : null}
            </Rise>
          )}

          <Rise i={2} className={`${PANEL} p-4`}>
            <div className="flex flex-wrap items-baseline justify-between gap-2">
              <SectionLabel icon="pi pi-list">Retrieved chunks</SectionLabel>
              <span className="text-xs tabular-nums text-ink-muted">
                {result.chunks.length} of {result.candidates ?? result.chunks.length} candidates · ~{result.estimatedContextTokens} tokens
              </span>
            </div>
            {result.chunks.length === 0 ? (
              <EmptyState icon="pi pi-filter" title="Nothing cleared the score floor"
                          hint="Every candidate scored below this version's minimum. Lower it, or index a source that covers the question." />
            ) : (
              <div className="mt-3 flex flex-col gap-2">
                {result.chunks.map((c, i) => (
                  <div key={c.chunkId} className="ui-inset ui-rise p-3" style={riseAt(i + 3)}>
                    <div className="mb-1.5 flex flex-wrap items-center gap-2 text-xs">
                      <span className="font-mono font-semibold text-indigo-600 dark:text-indigo-400">[{c.sourceIndex ?? c.rank}]</span>
                      {c.rerankScore != null && (
                        <span className="ui-tone ui-tone--info tabular-nums" title="Relevance from the reranker">rerank {Math.round(c.rerankScore * 100)}%</span>
                      )}
                      {c.score != null
                        ? <span className="tabular-nums font-semibold text-ink" title="Retrieval similarity">{(c.score * 100).toFixed(1)}%</span>
                        : <span className="ui-tone ui-tone--neutral" title="Found by text search only; no similarity score">text match</span>}
                      <span className="font-mono text-ink-muted">{c.source || '—'}</span>
                      {c.page != null && <span className="tabular-nums text-ink-muted">p.{c.page}</span>}
                      {c.section && <span className="italic text-ink-muted">{c.section}</span>}
                    </div>
                    <p className="whitespace-pre-wrap text-sm leading-relaxed text-gray-700 dark:text-gray-200">{c.content}</p>
                    {/* The passage's own pictures. They ride along on the chunk's
                        metadata, so showing them costs no extra request and no
                        second embedding space — see utils/source-images. */}
                    <ChunkImages images={collectDocImages(null, c.metadata)} />
                  </div>
                ))}
              </div>
            )}
          </Rise>

          {result.contextSent && (
            <Rise i={3}>
              <details className={`${PANEL} p-4`}>
                <summary className="ui-press cursor-pointer select-none text-[11px] font-semibold uppercase tracking-[0.06em] text-ink-muted">
                  Context sent to the model
                </summary>
                <pre className="ui-inset mt-3 max-h-96 overflow-auto p-3 text-xs leading-relaxed whitespace-pre-wrap text-gray-700 dark:text-gray-200">{result.contextSent}</pre>
              </details>
            </Rise>
          )}
        </>
      )}
    </div>
  );
};

export default RagPlayground;
